/**
 * Playback Map — Canvas 2D city renderer with animated entities and VFX.
 * Renders a simplified city_graph overlay with timeline-driven entity sprites
 * and event visual effects. Designed for the Playback screen (#40).
 */

import {
  CityGraph, CityNode, Sector,
  CW, CH, TILE_SIZE, WORLD_W, WORLD_H, INIT_ZOOM,
  TYPE_BG, toWorldX, toWorldY,
} from '../map/types';
import { MovementRecord, EventRecord, TimelineOutput } from '../data/timeline';
import { fixture } from '../data/fixture';

// ── Entity state ─────────────────────────────────────────────────────────

interface EntityState {
  id: string;
  x: number;
  y: number;
  visible: boolean;
  name: string;
}

// ── VFX state ────────────────────────────────────────────────────────────

interface VfxParticle {
  x: number;
  y: number;
  type: 'assault' | 'hack' | 'cash' | 'arrest';
  text?: string;
  spawnTick: number;
  /** Duration in ticks the VFX stays visible. */
  duration: number;
}

// ── Road fill constants ──────────────────────────────────────────────────

/** Canvas accent colour from the live --ac token (CSS vars don't work in ctx). */
const accent = (a: number): string =>
  `rgba(${getComputedStyle(document.documentElement).getPropertyValue('--ac').trim() || '0,245,212'}, ${a})`;

const ROAD_FILL_AVENUE = '#1a2438';
const ROAD_FILL_STREET = '#151c2a';

// ── PlaybackMap ──────────────────────────────────────────────────────────

export class PlaybackMap {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private graph: CityGraph | null = null;
  private nodeMap = new Map<string, CityNode>();

  // Camera
  private camera = { x: 0, y: 0, zoom: INIT_ZOOM };
  private isPanning = false;
  private lastMX = 0;
  private lastMY = 0;

  // Timeline data
  private movements: MovementRecord[] = [];
  private movementsByEntity = new Map<string, MovementRecord[]>();
  private events: EventRecord[] = [];
  private entities = new Map<string, EntityState>();
  private vfxParticles: VfxParticle[] = [];
  private firedEventIds = new Set<string>();

  // Current playback tick (set externally)
  private _tick = 0;

  /** Called when user clicks on an entity sprite. */
  onEntityClick: ((entityId: string) => void) | null = null;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = CW;
    this.canvas.height = CH;
    this.canvas.style.cssText = 'display:block;width:100%;height:100%;cursor:grab;image-rendering:pixelated';
    this.ctx = this.canvas.getContext('2d')!;

    this.setupInputHandlers();
    this.showLoading();
  }

  // ── Public API ─────────────────────────────────────────────────────────

  get tick(): number { return this._tick; }

  /** Get current entity state by ID (for sidebar live updates). */
  getEntity(id: string): EntityState | undefined { return this.entities.get(id); }

  /** Get all entity IDs. */
  getEntityIds(): string[] { return [...this.entities.keys()]; }

  /** Resolve a world coordinate to the nearest sector_id. */
  resolveNodeSector(nodeId: string): string {
    const m = nodeId.match(/^(S-\d+)/);
    return m ? m[1] : nodeId;
  }

  /** Find the current movement for an entity at the current tick. */
  getActiveMovement(entityId: string): MovementRecord | null {
    const t = this._tick;
    for (const m of this.movements) {
      if (m.entity_id === entityId && t >= m.tick_start && t <= m.tick_end) return m;
    }
    return null;
  }

  setTick(t: number): void {
    const oldTick = this._tick;
    this._tick = t;

    // If scrubbing backwards, reset fired events
    if (t < oldTick) {
      this.firedEventIds.clear();
      this.vfxParticles = [];
    }

    this.spawnVfx();
    this.interpolateEntities();
    this.draw();
  }

  /** Reset fired events (call when scrubbing to arbitrary position). */
  resetEvents(): void {
    this.firedEventIds.clear();
    this.vfxParticles = [];
  }

  setTimeline(tl: TimelineOutput): void {
    this.movements = tl.movements;
    this.events = tl.events;

    // Build per-entity movement index (sorted by tick_end for fast parked-position lookup)
    this.movementsByEntity.clear();
    for (const m of tl.movements) {
      let arr = this.movementsByEntity.get(m.entity_id);
      if (!arr) { arr = []; this.movementsByEntity.set(m.entity_id, arr); }
      arr.push(m);
    }
    for (const arr of this.movementsByEntity.values()) {
      arr.sort((a, b) => a.tick_end - b.tick_end);
    }

    // Initialize entity states
    this.entities.clear();
    for (const entityId of this.movementsByEntity.keys()) {
      this.entities.set(entityId, {
        id: entityId,
        x: 0, y: 0,
        visible: false,
        name: this.resolveRunnerName(entityId),
      });
    }
  }

  async loadGraph(): Promise<void> {
    const { mapData } = await import('../data/fixture');
    if (!mapData?.cityGraph) {
      this.ctx.clearRect(0, 0, CW, CH);
      this.ctx.fillStyle = '#f72585';
      this.ctx.font = '12px JetBrains Mono, monospace';
      this.ctx.textAlign = 'center';
      this.ctx.fillText('Keine Kartendaten vom Server erhalten', CW / 2, CH / 2);
      return;
    }
    this.graph = mapData.cityGraph as CityGraph;
    for (const s of this.graph.sectors) {
      for (const n of s.nodes) this.nodeMap.set(n.node_id, n);
    }
    this.draw();
  }

  // ── Entity interpolation ───────────────────────────────────────────────

  private interpolateEntities(): void {
    const t = this._tick;

    // Reset all to invisible
    for (const e of this.entities.values()) e.visible = false;

    // For each entity, find the active movement segment
    for (const m of this.movements) {
      if (t < m.tick_start || t > m.tick_end) continue;
      const entity = this.entities.get(m.entity_id);
      if (!entity) continue;

      const fromNode = this.nodeMap.get(m.from_node);
      const toNode = this.nodeMap.get(m.to_node);
      if (!fromNode || !toNode) continue;

      const duration = m.tick_end - m.tick_start;
      const progress = duration > 0 ? (t - m.tick_start) / duration : 1;
      const clamped = Math.max(0, Math.min(1, progress));

      // Path interpolation (multi-segment if path array exists)
      if (m.path && m.path.length >= 2) {
        this.interpolatePath(entity, m.path, clamped);
      } else {
        // Simple lerp between from and to
        entity.x = toWorldX(fromNode.global_x) + (toWorldX(toNode.global_x) - toWorldX(fromNode.global_x)) * clamped;
        entity.y = toWorldY(fromNode.global_y) + (toWorldY(toNode.global_y) - toWorldY(fromNode.global_y)) * clamped;
      }
      entity.visible = true;
    }

    // Entities not in any movement segment: park at last known position
    for (const e of this.entities.values()) {
      if (e.visible) continue;
      const moves = this.movementsByEntity.get(e.id);
      if (!moves) continue;
      // Binary search: find last movement with tick_end <= t (array sorted by tick_end)
      let lo = 0, hi = moves.length - 1, best = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >>> 1;
        if (moves[mid].tick_end <= t) { best = mid; lo = mid + 1; }
        else hi = mid - 1;
      }
      if (best >= 0) {
        const lastNode = this.nodeMap.get(moves[best].to_node);
        if (lastNode) {
          e.x = toWorldX(lastNode.global_x);
          e.y = toWorldY(lastNode.global_y);
          e.visible = true;
        }
      }
    }
  }

  private interpolatePath(entity: EntityState, path: string[], progress: number): void {
    const segments = path.length - 1;
    const segF = progress * segments;
    const segIdx = Math.min(Math.floor(segF), segments - 1);
    const segProgress = segF - segIdx;

    const a = this.nodeMap.get(path[segIdx]);
    const b = this.nodeMap.get(path[segIdx + 1]);
    if (!a || !b) return;

    entity.x = toWorldX(a.global_x) + (toWorldX(b.global_x) - toWorldX(a.global_x)) * segProgress;
    entity.y = toWorldY(a.global_y) + (toWorldY(b.global_y) - toWorldY(a.global_y)) * segProgress;
  }

  // ── VFX spawning ───────────────────────────────────────────────────────

  private spawnVfx(): void {
    const t = Math.floor(this._tick);
    for (const e of this.events) {
      if (e.tick > t) continue;
      if (this.firedEventIds.has(e.id)) continue;
      this.firedEventIds.add(e.id);

      const node = this.nodeMap.get(e.node_id);
      if (!node) continue;

      const x = toWorldX(node.global_x);
      const y = toWorldY(node.global_y);

      const evType = e.event_type.toUpperCase();
      if (evType.includes('ASSAULT') || evType.includes('DEFEND') || evType.includes('RAID')) {
        this.vfxParticles.push({ x, y, type: 'assault', spawnTick: e.tick, duration: 40 });
      } else if (evType.includes('HACK')) {
        this.vfxParticles.push({ x, y, type: 'hack', spawnTick: e.tick, duration: 35 });
      } else if (evType === 'ARRESTED' || e.result === 'ARRESTED') {
        this.vfxParticles.push({ x, y, type: 'arrest', spawnTick: e.tick, duration: 50 });
      }

      // Cash delta floating text
      if (e.delta_cash && Math.abs(e.delta_cash) > 0) {
        const sign = e.delta_cash > 0 ? '+' : '';
        this.vfxParticles.push({
          x, y: y - TILE_SIZE * 0.5,
          type: 'cash',
          text: `${sign}$${Math.round(e.delta_cash).toLocaleString('de-DE')}`,
          spawnTick: e.tick,
          duration: 60,
        });
      }
    }

    // Expire old VFX
    this.vfxParticles = this.vfxParticles.filter(p => this._tick - p.spawnTick < p.duration);
  }

  // ── Drawing ────────────────────────────────────────────────────────────

  private draw(): void {
    const { ctx } = this;
    const g = this.graph;
    if (!g) return;

    const { grid_cols, grid_rows } = g.meta;
    const cellW = WORLD_W / grid_cols;
    const cellH = WORLD_H / grid_rows;

    // Viewport culling bounds (world coords)
    const vpL = -this.camera.x / this.camera.zoom;
    const vpT = -this.camera.y / this.camera.zoom;
    const vpR = vpL + CW / this.camera.zoom;
    const vpB = vpT + CH / this.camera.zoom;

    function isVisible(s: Sector) {
      const sx = s.grid_col * cellW, sy = s.grid_row * cellH;
      return sx + cellW >= vpL && sx <= vpR && sy + cellH >= vpT && sy <= vpB;
    }

    ctx.clearRect(0, 0, CW, CH);
    ctx.save();
    ctx.translate(this.camera.x, this.camera.y);
    ctx.scale(this.camera.zoom, this.camera.zoom);

    // L0: Dark base
    ctx.fillStyle = '#080b12';
    ctx.fillRect(0, 0, WORLD_W, WORLD_H);

    // L1: Sector backgrounds
    for (const s of g.sectors) {
      if (!isVisible(s)) continue;
      ctx.fillStyle = TYPE_BG[s.sector_type] ?? '#111827';
      ctx.fillRect(s.grid_col * cellW, s.grid_row * cellH, cellW, cellH);
    }

    // L2: Subtle grid lines
    ctx.strokeStyle = accent(0.06);
    ctx.lineWidth = 1 / this.camera.zoom;
    for (const s of g.sectors) {
      if (!isVisible(s)) continue;
      ctx.strokeRect(s.grid_col * cellW, s.grid_row * cellH, cellW, cellH);
    }

    // L3: Road tiles (simplified — asphalt fill only)
    const TS = TILE_SIZE;
    const halfT = TS / 2;
    // Sidewalk pass
    ctx.fillStyle = '#1e2636';
    const pad = TS * 0.08;
    for (const s of g.sectors) {
      if (!isVisible(s)) continue;
      for (const n of s.nodes) {
        if (n.node_type !== 'AVENUE' && n.node_type !== 'STREET') continue;
        ctx.fillRect(toWorldX(n.global_x) - halfT - pad, toWorldY(n.global_y) - halfT - pad,
                     TS + pad * 2, TS + pad * 2);
      }
    }
    // Asphalt pass
    for (const s of g.sectors) {
      if (!isVisible(s)) continue;
      for (const n of s.nodes) {
        if (n.node_type !== 'AVENUE' && n.node_type !== 'STREET') continue;
        ctx.fillStyle = n.node_type === 'AVENUE' ? ROAD_FILL_AVENUE : ROAD_FILL_STREET;
        ctx.fillRect(toWorldX(n.global_x) - halfT, toWorldY(n.global_y) - halfT, TS, TS);
      }
    }

    // L4: Building footprints (simplified colored squares)
    const bldgS = TS * 0.88;
    const halfS = bldgS / 2;
    ctx.fillStyle = accent(0.12);
    for (const s of g.sectors) {
      if (!isVisible(s)) continue;
      for (const n of s.nodes) {
        if (n.node_type !== 'BUILDING_ENTRY') continue;
        const bx = toWorldX(n.global_x) - halfS;
        const by = toWorldY(n.global_y) - halfS;
        ctx.fillRect(bx, by, bldgS, bldgS);
      }
    }

    // L5: Sector labels
    const fontSize = Math.max(8, Math.min(TILE_SIZE * 2.5, 80 / this.camera.zoom));
    ctx.font = `bold ${fontSize}px JetBrains Mono, monospace`;
    ctx.textAlign = 'center';
    for (const s of g.sectors) {
      if (!isVisible(s)) continue;
      const cx = (s.grid_col + 0.5) * cellW;
      const cy = (s.grid_row + 0.5) * cellH;
      ctx.fillStyle = 'rgba(255,255,255,0.18)';
      ctx.fillText(s.sector_id, cx, cy);
    }

    // ── L6: Entity sprites ───────────────────────────────────────────────
    this.drawEntities(vpL, vpT, vpR, vpB);

    // ── L7: VFX particles ────────────────────────────────────────────────
    this.drawVfx(vpL, vpT, vpR, vpB);

    ctx.restore();
  }

  private drawEntities(vpL: number, vpT: number, vpR: number, vpB: number): void {
    const { ctx } = this;
    const r = TILE_SIZE * 0.35;
    const zoom = this.camera.zoom;

    for (const e of this.entities.values()) {
      if (!e.visible) continue;
      // Viewport culling
      if (e.x < vpL - r || e.x > vpR + r || e.y < vpT - r || e.y > vpB + r) continue;

      // Glow
      ctx.save();
      ctx.shadowColor = accent(1);
      ctx.shadowBlur = 8 / zoom;

      // Filled circle
      ctx.fillStyle = accent(0.85);
      ctx.beginPath();
      ctx.arc(e.x, e.y, r, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();

      // Outline
      ctx.strokeStyle = accent(1);
      ctx.lineWidth = 2 / zoom;
      ctx.beginPath();
      ctx.arc(e.x, e.y, r, 0, Math.PI * 2);
      ctx.stroke();

      // Name label (only at high zoom)
      if (zoom * TILE_SIZE >= 16) {
        ctx.fillStyle = accent(1);
        ctx.font = `bold ${Math.max(8, 14 / zoom)}px JetBrains Mono, monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(e.name, e.x, e.y - r - 6 / zoom);
      }
    }
  }

  private drawVfx(vpL: number, vpT: number, vpR: number, vpB: number): void {
    const { ctx } = this;
    const zoom = this.camera.zoom;

    for (const p of this.vfxParticles) {
      // Viewport culling
      // Margin must cover max VFX radius (assault ring = TILE_SIZE * 3)
      const margin = TILE_SIZE * 3;
      if (p.x < vpL - margin || p.x > vpR + margin || p.y < vpT - margin || p.y > vpB + margin) continue;

      const age = this._tick - p.spawnTick;
      const life = Math.max(0, 1 - age / p.duration); // 1→0

      switch (p.type) {
        case 'assault': {
          // Red expanding ring
          const radius = TILE_SIZE * (1 + (1 - life) * 2);
          ctx.strokeStyle = `rgba(247,37,133,${(life * 0.8).toFixed(2)})`;
          ctx.lineWidth = 3 / zoom;
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.stroke();
          // Inner flash
          if (life > 0.5) {
            ctx.fillStyle = `rgba(247,37,133,${((life - 0.5) * 0.4).toFixed(2)})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, TILE_SIZE * 0.8, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 'hack': {
          // Cyan pulsing ring
          const pulse = Math.sin(age * 0.5) * 0.3 + 0.7;
          const radius = TILE_SIZE * (0.8 + pulse * 0.5);
          ctx.strokeStyle = accent(life * 0.7);
          ctx.lineWidth = 2 / zoom;
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.stroke();
          // Inner glow
          ctx.fillStyle = accent(life * 0.1);
          ctx.beginPath();
          ctx.arc(p.x, p.y, TILE_SIZE * 0.6, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case 'arrest': {
          // Amber flashing
          const flash = Math.sin(age * 1.2) > 0 ? 1 : 0.3;
          ctx.fillStyle = `rgba(255,193,7,${(life * 0.3 * flash).toFixed(2)})`;
          ctx.fillRect(p.x - TILE_SIZE, p.y - TILE_SIZE, TILE_SIZE * 2, TILE_SIZE * 2);
          break;
        }
        case 'cash': {
          // Floating text rising upward
          const rise = (1 - life) * TILE_SIZE * 2;
          ctx.fillStyle = accent(life);
          ctx.font = `bold ${Math.max(10, 18 / zoom)}px JetBrains Mono, monospace`;
          ctx.textAlign = 'center';
          ctx.fillText(p.text ?? '', p.x, p.y - rise);
          break;
        }
      }
    }
  }

  // ── Input handlers ─────────────────────────────────────────────────────

  private setupInputHandlers(): void {
    const c = this.canvas;
    let dragStartX = 0, dragStartY = 0, didDrag = false;

    c.addEventListener('mousedown', (e) => {
      this.isPanning = true;
      this.lastMX = e.clientX;
      this.lastMY = e.clientY;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      didDrag = false;
      c.style.cursor = 'grabbing';
    });

    c.addEventListener('mousemove', (e) => {
      if (!this.isPanning) return;
      const dx = e.clientX - dragStartX, dy = e.clientY - dragStartY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag = true;
      const r = c.getBoundingClientRect();
      this.camera.x += (e.clientX - this.lastMX) * CW / r.width;
      this.camera.y += (e.clientY - this.lastMY) * CH / r.height;
      this.lastMX = e.clientX;
      this.lastMY = e.clientY;
      this.clampPan();
      this.draw();
    });

    c.addEventListener('mouseup', (e) => {
      this.isPanning = false;
      c.style.cursor = 'grab';
      if (!didDrag && this.onEntityClick) {
        this.handleEntityClick(e);
      }
    });

    c.addEventListener('mouseleave', () => {
      this.isPanning = false;
      c.style.cursor = 'grab';
    });

    c.addEventListener('wheel', (e) => {
      e.preventDefault();
      const r = c.getBoundingClientRect();
      const mx = (e.clientX - r.left) * CW / r.width;
      const my = (e.clientY - r.top) * CH / r.height;
      const factor = e.deltaY < 0 ? 1.15 : 0.87;
      const newZoom = Math.min(Math.max(this.camera.zoom * factor, INIT_ZOOM * 0.5), 2.0);
      this.camera.x = mx - (mx - this.camera.x) * (newZoom / this.camera.zoom);
      this.camera.y = my - (my - this.camera.y) * (newZoom / this.camera.zoom);
      this.camera.zoom = newZoom;
      this.clampPan();
      this.draw();
    }, { passive: false });
  }

  private handleEntityClick(e: MouseEvent): void {
    const c = this.canvas;
    const rect = c.getBoundingClientRect();
    // Convert screen coords → canvas coords → world coords
    const canvasX = (e.clientX - rect.left) * CW / rect.width;
    const canvasY = (e.clientY - rect.top) * CH / rect.height;
    const worldX = (canvasX - this.camera.x) / this.camera.zoom;
    const worldY = (canvasY - this.camera.y) / this.camera.zoom;

    const hitRadius = TILE_SIZE * 0.6; // slightly larger than visual radius for easier clicks
    let bestDist = hitRadius;
    let bestId: string | null = null;

    for (const ent of this.entities.values()) {
      if (!ent.visible) continue;
      const dx = ent.x - worldX, dy = ent.y - worldY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < bestDist) {
        bestDist = dist;
        bestId = ent.id;
      }
    }

    if (bestId && this.onEntityClick) {
      this.onEntityClick(bestId);
    }
  }

  private clampPan(): void {
    const maxPanX = WORLD_W * this.camera.zoom - CW;
    const maxPanY = WORLD_H * this.camera.zoom - CH;
    this.camera.x = Math.min(0, Math.max(-maxPanX, this.camera.x));
    this.camera.y = Math.min(0, Math.max(-maxPanY, this.camera.y));
  }

  private showLoading(): void {
    this.ctx.fillStyle = '#0a0c14';
    this.ctx.fillRect(0, 0, CW, CH);
    this.ctx.fillStyle = accent(0.3);
    this.ctx.font = '12px JetBrains Mono, monospace';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('Lade Karte…', CW / 2, CH / 2);
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  private resolveRunnerName(entityId: string): string {
    const match = entityId.match(/^p\d+_h(\d+)$/);
    if (!match) return entityId;
    const idx = parseInt(match[1], 10);
    const runners = fixture?.player?.runners;
    if (runners && idx < runners.length) return runners[idx].name;
    return entityId;
  }
}
